/**
 * format.ts — Intl wrappers for dates, numbers, currencies.
 * Per frontend-conventions.md §13: never hardcode date/number formats.
 */

import { currentLocale } from './i18n';

export function formatDate(
  date: Date | string | number,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string {
  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  return new Intl.DateTimeFormat(currentLocale(), options).format(d);
}

export function formatDateTime(
  date: Date | string | number,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
): string {
  return formatDate(date, options);
}

export function formatRelativeTime(date: Date | string | number): string {
  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const diffMs = d.getTime() - Date.now();
  const diffSec = Math.round(diffMs / 1000);
  const rtf = new Intl.RelativeTimeFormat(currentLocale(), { numeric: 'auto' });

  const absSec = Math.abs(diffSec);
  if (absSec < 60) return rtf.format(diffSec, 'second');
  if (absSec < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
  if (absSec < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour');
  if (absSec < 2_592_000) return rtf.format(Math.round(diffSec / 86400), 'day');
  if (absSec < 31_536_000) return rtf.format(Math.round(diffSec / 2_592_000), 'month');
  return rtf.format(Math.round(diffSec / 31_536_000), 'year');
}

export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(currentLocale(), options).format(value);
}

export function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat(currentLocale(), {
    style: 'currency',
    currency,
  }).format(amount);
}

/**
 * Build a single display string from a person's first and last name. Used
 * for users, agents, and agent_templates per the person-name-split spec.
 */
export function formatPersonName(person: {
  first_name: string;
  last_name?: string | null;
}): string {
  return person.last_name ? `${person.first_name} ${person.last_name}` : person.first_name;
}

/**
 * Format a job's elapsed time from startedAt to now.
 * Returns "< 1m", "45m", "4h 12m", or "2h".
 */
export function formatElapsedTime(startedAt: string): string {
  const ms = Date.now() - Date.parse(startedAt);
  if (ms < 60_000) return '< 1m';
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}
